import { useCallback } from 'react';
import intl from 'react-intl-universal';
import { showNotification as showSnackbarNotification } from 'state/SnackbarNotification/SnackbarNotificationSlice';
import { useAppThunkDispatch } from 'state/store';

interface NotificationConfig {
  successTitle?: string;
  successMessage: string;
  errorTitle?: string;
  errorMessage?: string;
}

interface ResultErrorConfig {
  errorTitle: string;
  errorMessage: string;
  onResultError?: () => void;
}

interface AsyncWithNotificationOptions<T> {
  asyncOperation: () => Promise<T>;
  notification: NotificationConfig;
  onSuccess?: (result: T) => void;
  onError?: (error: unknown) => void;
  /**
   * If true or returns true, the success notification will be skipped.
   * Useful when success notification depends on additional conditions.
   */
  skipSuccessNotification?: boolean | ((result: T) => boolean);
  /**
   * Validates the result and returns error config if the result indicates an error.
   * Useful when the async operation returns an error in the response instead of throwing.
   * Return undefined/null if the result is valid, or ResultErrorConfig if it's an error.
   */
  validateResult?: (result: T) => ResultErrorConfig | undefined | null;
}

/**
 * Hook that wraps async operations with snackbar notifications.
 * Automatically shows success/error notifications based on the operation result.
 *
 * @example
 * // Basic usage
 * const { executeWithNotification } = useAsyncWithNotification();
 *
 * await executeWithNotification({
 *   asyncOperation: () => dispatch(updateRequest({ id, changes })),
 *   notification: {
 *     successMessage: intl.get('REQUEST_APPROVED_MESSAGE'),
 *   },
 *   onSuccess: () => navigate('/requests'),
 * });
 *
 * @example
 * // Conditional success notification
 * await executeWithNotification({
 *   asyncOperation: () => dispatch(updateRequest({ id, changes })),
 *   notification: {
 *     successMessage: intl.get('CANCELLATION_SUCCESS'),
 *   },
 *   skipSuccessNotification: requestSliceStatus !== 'idle',
 *   onSuccess: () => {
 *     if (requestSliceStatus === 'idle') {
 *       handleLeavingPage(PATHS.REQUESTS_LIST_PAGE);
 *     }
 *   },
 * });
 */
const useAsyncWithNotification = () => {
  const dispatch = useAppThunkDispatch();

  const executeWithNotification = useCallback(
    async <T>({
      asyncOperation,
      notification,
      onSuccess,
      onError,
      skipSuccessNotification = false,
      validateResult,
    }: AsyncWithNotificationOptions<T>): Promise<void> => {
      try {
        const result = await asyncOperation();

        // Check if result indicates an error (e.g., res.error)
        const resultError = validateResult?.(result);
        if (resultError) {
          dispatch(
            showSnackbarNotification({
              notificationVariant: 'error',
              notificationTitle: resultError.errorTitle,
              notificationMessage: resultError.errorMessage,
            })
          );
          resultError.onResultError?.();
        }

        const shouldSkip =
          typeof skipSuccessNotification === 'function'
            ? skipSuccessNotification(result)
            : skipSuccessNotification;

        if (!shouldSkip) {
          dispatch(
            showSnackbarNotification({
              notificationVariant: 'success',
              notificationTitle:
                notification.successTitle ?? intl.get('SUCCESS_SNACKBAR_TITLE'),
              notificationMessage: notification.successMessage,
            })
          );
        }
        onSuccess?.(result);
      } catch (error) {
        dispatch(
          showSnackbarNotification({
            notificationVariant: 'error',
            notificationTitle:
              notification.errorTitle ?? intl.get('ERROR_SNACKBAR_TITLE'),
            notificationMessage:
              notification.errorMessage ?? intl.get('ERROR_SNACKBAR_MESSAGE'),
          })
        );
        onError?.(error);
      }
    },
    [dispatch]
  );

  return { executeWithNotification };
};

export default useAsyncWithNotification;
